import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE } from "@/config/site";
import { canonicalPath } from "@/lib/canonicalPath";
import { useLanguage } from "@/context/LanguageContext";
import { basePath, localizePath } from "@/i18n/locale";
import { absoluteSeoImage, buildPageSeoSchemas } from "@/config/seoSchema";
export { localizeSeoJsonLd } from "@/config/seoSchema";

// Imperative per-page head management. Guarantees the rendered DOM carries the
// correct title, description, canonical, robots, OG/Twitter and JSON-LD on both
// direct load and client-side navigation (no dependency on a head library).
function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((other) => { if (other !== el) other.remove(); });
}

function upsertCanonical(href) {
  if (!href) {
    document.head.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    return;
  }
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  document.head.querySelectorAll('link[rel="canonical"]').forEach((other) => { if (other !== el) other.remove(); });
}

function setAlternates(pathname, noindex) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  if (noindex) return;
  for (const [language, locale] of [["en", "en"], ["hi", "hi"], ["x-default", "en"]]) {
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = language;
    el.href = `${SITE.domain}${localizePath(basePath(pathname), locale)}`;
    document.head.appendChild(el);
  }
}

function setJsonLd(data) {
  document.head.querySelectorAll('script[data-seo-jsonld="1"]').forEach((s) => s.remove());
  if (!data) return;
  [].concat(data).forEach((d) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo-jsonld", "1");
    s.text = JSON.stringify(d);
    document.head.appendChild(s);
  });
}

export const Seo = ({ title, description, image, type = "website", jsonLd, noindex = false }) => {
  const { pathname } = useLocation();
  const { locale, t } = useLanguage();

  useEffect(() => {
    const canonical = `${SITE.domain}${canonicalPath(localizePath(pathname, locale))}`;
    const isPreview =
      typeof window !== "undefined" &&
      !/(^|\.)touroperatorservices\.com$/.test(window.location.hostname);
    const robots = noindex || isPreview ? "noindex, nofollow" : "index, follow";
    const fullTitle = t(title || `${SITE.brand} | Taxi & Tempo Traveller in Satna`);
    const desc = t(
      description ||
      "Local, outstation and group taxi & Tempo Traveller service in Satna and across Madhya Pradesh. Call or WhatsApp for a quote."
    );

    document.documentElement.lang = locale;
    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertMeta("name", "robots", robots);
    upsertCanonical(noindex ? null : canonical);
    setAlternates(pathname, noindex);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:site_name", SITE.brand);
    upsertMeta("property", "og:locale", locale === "hi" ? "hi_IN" : "en_IN");
    upsertMeta("property", "og:locale:alternate", locale === "hi" ? "en_IN" : "hi_IN");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    const imageUrl = absoluteSeoImage(image);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("name", "twitter:image", imageUrl);
    setJsonLd(buildPageSeoSchemas({ pathname, title: fullTitle, description: desc, image: imageUrl, jsonLd, locale, noindex }));
  }, [pathname, title, description, image, type, jsonLd, noindex, locale, t]);

  return null;
};
